'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { FeedbackDialog } from './FeedbackDialog';

interface FeedbackContextType {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
}

const FeedbackContext = createContext<FeedbackContextType | null>(null);

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within FeedbackProvider');
  return ctx;
}

interface FeedbackProviderProps { children: React.ReactNode }

export function FeedbackProvider({ children }: FeedbackProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((v) => !v);

  useEffect(() => {
    const onFeedbackOpen = () => setIsOpen(true);
    const onFeedbackHotkey = () => toggle();
    window.addEventListener('feedback:open', onFeedbackOpen);
    window.addEventListener('hotkey:feedback.toggle', onFeedbackHotkey);
    return () => {
      window.removeEventListener('feedback:open', onFeedbackOpen);
      window.removeEventListener('hotkey:feedback.toggle', onFeedbackHotkey);
    };
  }, [toggle]);

  const value: FeedbackContextType = { open, close, toggle, isOpen };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackDialog open={isOpen} onOpenChange={setIsOpen} />
    </FeedbackContext.Provider>
  );
}

export default FeedbackProvider;
